import React from 'react';
import { Box, Typography } from '@mui/material';

/**
 * Pure SVG line chart showing SP/DevDay throughput over sprint history.
 * No external chart library required.
 */
export default function VelocityChart({ sprints = [] }) {
  const completed = sprints.filter(
    (s) => s.status === 'completed' && s.actual_sp != null && s.total_planned_devdays > 0
  );

  if (completed.length === 0) return null;

  const points = completed.map((s, i) => ({
    x: i,
    y: s.actual_sp / s.total_planned_devdays,
    label: s.name,
  }));

  const W = 560;
  const H = 160;
  const PAD = { top: 16, right: 24, bottom: 36, left: 48 };
  const chartW = W - PAD.left - PAD.right;
  const chartH = H - PAD.top - PAD.bottom;

  const minY = 0;
  const maxY = Math.max(...points.map((p) => p.y)) * 1.2 || 1;
  const xScale = (i) => points.length === 1 ? chartW / 2 : (i / (points.length - 1)) * chartW;
  const yScale = (v) => chartH - ((v - minY) / (maxY - minY)) * chartH;

  const pathD = points
    .map((p, i) => `${i === 0 ? 'M' : 'L'} ${xScale(i).toFixed(1)} ${yScale(p.y).toFixed(1)}`)
    .join(' ');

  const yTicks = 4;

  return (
    <Box>
      <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
        SP / Dev Day — over time
      </Typography>
      <svg width="100%" viewBox={`0 0 ${W} ${H}`} style={{ maxWidth: W, overflow: 'visible' }}>
        <g transform={`translate(${PAD.left}, ${PAD.top})`}>
          {/* Y-axis gridlines + labels */}
          {Array.from({ length: yTicks + 1 }, (_, i) => {
            const v = minY + ((maxY - minY) * i) / yTicks;
            const cy = yScale(v);
            return (
              <g key={i}>
                <line x1={0} y1={cy} x2={chartW} y2={cy} stroke="#e0e0e0" strokeWidth={1} />
                <text x={-6} y={cy} textAnchor="end" dominantBaseline="middle" fontSize={10} fill="#9e9e9e">
                  {v.toFixed(1)}
                </text>
              </g>
            );
          })}

          {/* Line */}
          <path d={pathD} fill="none" stroke="#1976d2" strokeWidth={2} strokeLinejoin="round" />

          {/* Dots + x-labels */}
          {points.map((p, i) => (
            <g key={i}>
              <circle cx={xScale(i)} cy={yScale(p.y)} r={4} fill="#1976d2" />
              <text
                x={xScale(i)}
                y={chartH + 16}
                textAnchor="middle"
                fontSize={9}
                fill="#757575"
              >
                {p.label.length > 10 ? p.label.substring(0, 10) + '…' : p.label}
              </text>
            </g>
          ))}

          {/* Axes */}
          <line x1={0} y1={0} x2={0} y2={chartH} stroke="#bdbdbd" strokeWidth={1} />
          <line x1={0} y1={chartH} x2={chartW} y2={chartH} stroke="#bdbdbd" strokeWidth={1} />
        </g>
      </svg>
    </Box>
  );
}
